/**
 * Helper functions for computing aggregated statistics across precincts,
 * subregions, and legislative districts. Extracted from the original App.jsx.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck — helpers operate over the loosely-typed embedded dashboard data.
import { D } from '@/data/dashboardData';
import type { MapRegionCatalog } from '@/api/geoRegions';

export const LD_NAMES: Record<number, string> = {
  12: "LD 12 — South/Urban",
  13: "LD 13 — Central/Rural",
  14: "LD 14 — East/Suburban",
};

// The turf catalog is passed in rather than imported. It is tenant data now
// (GET /geo/regions via MapRegionsContext), so these helpers cannot reach for
// it at module scope without re-introducing the hardcoded-Platte bug; taking
// it as an argument also keeps them pure and callable from a test with a
// fixture catalog. A turf key the catalog does not know yields empty stats,
// which is the same answer these returned for an unknown key before.
export const getSubregionPrecincts = (sr, regions: MapRegionCatalog) => {
  const region = regions.byKey[sr];
  if (!region) return [];
  return D.precincts.filter(p => region.precincts.includes(p.precinct_num));
};

export const computeSRStats = (sr, regions: MapRegionCatalog) => {
  const precs = getSubregionPrecincts(sr, regions);
  if (!precs.length) return { total: 0, active: 0, active_pct: "0", avg_score: "0", avg_age: 0, primary_voters: 0, super_voter_pct: "0", primary_voter_pct: "0", tiers: {}, universes: {} };
  
  const total = precs.reduce((s, p) => s + p.total, 0);
  const active = precs.reduce((s, p) => s + p.active, 0);
  const primary_voters = precs.reduce((s, p) => s + p.primary_voters, 0);
  
  const tiers = { "Super Voter": 0, "Regular": 0, "Occasional": 0, "Rare": 0, "Never Voted": 0 };
  const universes = { "Persuasion": 0, "Persuasion+Mobilization": 0, "Mobilization": 0, "Base": 0, "Low Priority": 0 };
  
  precs.forEach(p => {
    Object.keys(tiers).forEach(k => { tiers[k] += p.tiers[k] || 0; });
    Object.keys(universes).forEach(k => { universes[k] += p.universes[k] || 0; });
  });
  
  return {
    total, active,
    active_pct: total ? (active / total * 100).toFixed(1) : "0",
    avg_score: total ? (precs.reduce((s, p) => s + p.avg_priority * p.total, 0) / total).toFixed(1) : "0",
    avg_age: total ? Math.round(precs.reduce((s, p) => s + p.avg_age * p.total, 0) / total) : 0,
    primary_voters,
    primary_voter_pct: total ? (primary_voters / total * 100).toFixed(1) : "0",
    super_voter_pct: total ? (precs.reduce((s, p) => s + p.total * p.super_voter_pct / 100, 0) / total * 100).toFixed(1) : "0",
    tiers, universes,
  };
};

export const computeLDStats = (ld, regions: MapRegionCatalog) => {
  const srs = regions.keys.filter(k => String(regions.byKey[k].ld) === String(ld));
  const srStats = srs.map(sr => computeSRStats(sr, regions));
  
  const total = srStats.reduce((s, st) => s + st.total, 0);
  const active = srStats.reduce((s, st) => s + st.active, 0);
  const primary_voters = srStats.reduce((s, st) => s + st.primary_voters, 0);
  
  const tiers = { "Super Voter": 0, "Regular": 0, "Occasional": 0, "Rare": 0, "Never Voted": 0 };
  const universes = { "Persuasion": 0, "Persuasion+Mobilization": 0, "Mobilization": 0, "Base": 0, "Low Priority": 0 };
  
  srStats.forEach(st => {
    Object.keys(tiers).forEach(k => { tiers[k] += st.tiers[k] || 0; });
    Object.keys(universes).forEach(k => { universes[k] += st.universes[k] || 0; });
  });
  
  return {
    total, active,
    active_pct: total ? (active / total * 100).toFixed(1) : "0",
    avg_score: total ? (D.precincts.filter(p => srs.some(sr => regions.byKey[sr].precincts.includes(p.precinct_num))).reduce((s, p) => s + p.avg_priority * p.total, 0) / total).toFixed(1) : "0",
    primary_voters,
    primary_voter_pct: total ? (primary_voters / total * 100).toFixed(1) : "0",
    subregions: srs.length,
    precincts: srs.flatMap(sr => regions.byKey[sr].precincts).length,
    tiers, universes,
  };
};

